import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { productionApi } from 'api/production';
import { Button } from 'shadcn/components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';
import { Input } from 'shadcn/components/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { cn } from 'shadcn/lib/utils';

/**
 * Example CSV data for uploading a product CSV.
 */
const EXAMPLE_CSV_DATA = [
    ['Carousel ID', 'Rank', 'External SKU'],
    ['17903', '', '114222'],
    ['17903', '3', '121004'],
    ['17903', '1', '114449'],
    ['17903', '', '119332'],
    ['17903', '2', '120157'],
    ['17906', '', '114449'],
    ['17906', '1', '118796'],
];

/**
 * Dialog for uploading a product CSV.
 *
 * @param {boolean} isOpen - Whether the dialog is open.
 * @param {Function} onClose - Function to close the dialog.
 * @param {Function} refetchCarousels - Function to refetch the carousels.
 */
export default function UploadProductCSVDialog({ isOpen, onClose, refetchCarousels }) {
    const [view, setView] = useState('upload');
    const [uploadResponse, setUploadResponse] = useState({ key: null, results: [] });

    useEffect(() => {
        if (!isOpen) {
            setView('upload');
            setUploadResponse({ key: null, results: [] });
        }
    }, [isOpen]);

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            {view === 'upload' && <UploadView onClose={onClose} onUpload={setUploadResponse} setView={setView} />}
            {view === 'confirm' && (
                <ConfirmView refetchCarousels={refetchCarousels} uploadResponse={uploadResponse} onClose={onClose} />
            )}
        </Dialog>
    );
}

/**
 * View for uploading a product CSV.
 *
 * @param {Function} onClose - Function to close the dialog.
 * @param {Function} onUpload - Function to upload the product CSV.
 * @param {Function} setView - Function to set the view.
 */
function UploadView({ onClose, onUpload, setView }) {
    const [exampleCSVUrl, setExampleCSVUrl] = useState(null);
    const [file, setFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const csvContent = EXAMPLE_CSV_DATA.map((row) => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        setExampleCSVUrl(url);

        return () => URL.revokeObjectURL(url);
    }, []);

    const handleUploadProductCSV = async (fileToUpload) => {
        setIsLoading(true);
        const toastID = toast.loading('Uploading product CSV...');

        try {
            const response = await productionApi.uploadCarousels({ file: fileToUpload });
            onUpload({ key: response.data.key, results: response.data.results });
            setView('confirm');
            toast.success('Product CSV uploaded successfully');
        } catch (error) {
            console.error(error);
            toast.error('Failed to upload product CSV');
        } finally {
            setIsLoading(false);
            toast.dismiss(toastID);
        }
    };

    return (
        <DialogContent>
            <DialogHeader>
                <DialogTitle>Upload Product CSV</DialogTitle>
                <DialogDescription>
                    Are you sure you want to upload a CSV of products? This will replace all existing products for
                    carousels included within the CSV.
                    <br />
                    <br />
                    You will need the relevant carousel IDs to build a product CSV.
                    <br />
                    <a
                        href={exampleCSVUrl}
                        download="storefront-config-example.csv"
                        className="text-primary! underline"
                    >
                        Download an example file.
                    </a>
                </DialogDescription>
            </DialogHeader>
            <Input
                className="hover:bg-muted hover:cursor-pointer"
                type="file"
                accept=".csv"
                onChange={(e) => setFile(e.target.files?.[0])}
            />
            <DialogFooter>
                <Button variant="outline" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="default" onClick={() => handleUploadProductCSV(file)} disabled={isLoading}>
                    Upload
                </Button>
            </DialogFooter>
        </DialogContent>
    );
}

/**
 * View for confirming a product CSV upload.
 *
 * @param {Function} onClose - Function to close the dialog.
 * @param {Function} refetchCarousels - Function to refetch the carousels.
 * @param {Object} uploadResponse - The response from the upload.
 */
function ConfirmView({ onClose, refetchCarousels, uploadResponse }) {
    const [isLoading, setIsLoading] = useState(false);

    const handleConfirmProductCSV = async () => {
        setIsLoading(true);
        const toastID = toast.loading('Confirming product CSV...');

        try {
            await productionApi.confirmCarouselUpload(uploadResponse.key);
            onClose();
            refetchCarousels();
            toast.success('Product CSV confirmed successfully');
        } catch (error) {
            console.error(error);
            toast.error('Failed to confirm product CSV');
        } finally {
            setIsLoading(false);
            toast.dismiss(toastID);
        }
    };

    return (
        <DialogContent className="flex flex-col sm:max-w-[90vw] w-fit max-h-[90vh]">
            <DialogHeader>
                <DialogTitle>Confirm Product CSV</DialogTitle>
                <DialogDescription>
                    Are you sure you want to upload this CSV of products?
                    <br />
                    This will replace all existing products for carousels included within the CSV.
                    <br />
                    <br />
                    <span className="font-bold">All affected carousels will be saved upon confirmation.</span>
                </DialogDescription>
            </DialogHeader>
            <Table className="h-full overflow-y-auto border rounded-md">
                <TableHeader>
                    <TableRow className="sticky top-0 border-b border-border bg-background">
                        <TableHead>Carousel ID</TableHead>
                        <TableHead>Rank</TableHead>
                        <TableHead>External SKU</TableHead>
                        <TableHead>Warnings</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {uploadResponse.results.map((result) => (
                        <TableRow
                            key={`${result.carouselID}-${result.externalSku}`}
                            className={cn(result.warnings.length > 0 && 'bg-destructive/20 hover:bg-destructive/30')}
                        >
                            <TableCell>{result.carouselID}</TableCell>
                            <TableCell>{result.rank}</TableCell>
                            <TableCell>{result.externalSku}</TableCell>
                            <TableCell>
                                {result.warnings.join('. ')}
                                {!!result.warnings.length && '.'}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <DialogFooter>
                <Button variant="outline" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="default" onClick={handleConfirmProductCSV} disabled={isLoading}>
                    Confirm
                </Button>
            </DialogFooter>
        </DialogContent>
    );
}
